<template>
  <div
    v-if="closestEvent && show"
    class="tw:border tw:p-3 tw:rounded-md tw:max-w-sm tw:flex tw:flex-col tw:gap-2"
    :class="isLive ? 'tw:bg-blue-000 tw:border-blue-500' : 'tw:bg-default-000 tw:border-black'"
  >
    <div class="tw:flex tw:justify-between tw:items-center">
      <div v-if="isLive" class="tw:flex tw:items-center tw:gap-2">
        <p class="tw:caption tw:text-red-500">{{ trans._livestorm.live }}</p>
        <div class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-red-500 tw:animate-pulse"></div>
      </div>
      <p v-else class="tw:caption tw:text-default-700">{{ trans._livestorm.upcoming }}</p>
      <EdroneButton type="link-text" size="small" @click="handleJoinClick">
        {{ isLive ? trans._livestorm.join_now : trans._livestorm.join_waitlist }}
      </EdroneButton>
    </div>
    <h3 class="tw:body-2--bold tw:text-black">{{ closestEvent.title }}</h3>
  </div>
</template>

<script lang="ts" setup>
import EdroneButton from "edrone-components/EdroneButton.vue";
import { onMounted, ref, computed, onUnmounted } from "vue";
import { getLiveStormEvents } from "vue-app/api/LiveStormService";
import { LiveStormEventInfo } from "vue-app/components/LiveStormEvents/LiveStormEvents.types";
import { dayjsWithTz } from "vue-app/utils/functions";

interface Props {
  show?: boolean;
}

const { show = true } = defineProps<Props>();

const closestEvent = ref<LiveStormEventInfo | null>(null);
const currentTimeInSeconds = ref(dayjsWithTz().unix());
let intervalId: ReturnType<typeof setInterval> | undefined;

const isLive = computed(() => {
  if (!closestEvent.value) {
    return false;
  }
  const eventStartTime = closestEvent.value.time;
  const eventEndTime = eventStartTime + closestEvent.value.duration * 60;
  return currentTimeInSeconds.value >= eventStartTime && currentTimeInSeconds.value < eventEndTime;
});

const handleJoinClick = () => {
  if (closestEvent.value) {
    const email = window.EDRONE_CONTEXT?.user?.email || "";
    const registrationLink = closestEvent.value.registration_link + "?email=" + email;
    window.open(registrationLink, "_blank");
  }
};

const shouldShowEvent = (event: LiveStormEventInfo) => {
  const isPaying = window.EDRONE_CONTEXT.tracker.isPaying;
  const description = event.description?.toLowerCase() || "";

  if (description.includes("#inspiration")) {
    return isPaying;
  }

  if (description.includes("#freemium") || description.includes("#activation")) {
    return !isPaying;
  }

  return false;
};

const sortEventsByLiveAndTime = (a: LiveStormEventInfo, b: LiveStormEventInfo, nowSeconds: number) => {
  const aIsLive = a.time <= nowSeconds;
  const bIsLive = b.time <= nowSeconds;

  if (aIsLive && !bIsLive) {
    return -1;
  }
  if (!aIsLive && bIsLive) {
    return 1;
  }

  return a.time - b.time;
};

const findClosestEvent = (events: LiveStormEventInfo[]) => {
  const userLang = window.EDRONE_CONTEXT.user.lang.toLowerCase();

  const nowSeconds = dayjsWithTz().unix();
  const ongoingOrUpcomingEvents = events
    .filter((event) => {
      const eventEndTime = event.time + event.duration * 60;
      return eventEndTime > nowSeconds && event.language.toLowerCase().includes(userLang);
    })
    .filter(shouldShowEvent)
    .sort((a, b) => sortEventsByLiveAndTime(a, b, nowSeconds));
  if (ongoingOrUpcomingEvents.length > 0) {
    closestEvent.value = ongoingOrUpcomingEvents[0];
  } else {
    closestEvent.value = null;
  }
};

function buildSessionsMap(includedData: any[]): Map<string, any> {
  const sessionsMap = new Map();
  for (const item of includedData) {
    if (item.type === "sessions") {
      sessionsMap.set(item.id, item);
    }
  }
  return sessionsMap;
}

async function loadAndProcessEvents() {
  const response = await getLiveStormEvents();
  const events: LiveStormEventInfo[] = [];

  const eventData = response.data;
  const includedData = response.included;

  let sessionsMap = new Map();
  if (includedData) {
    sessionsMap = buildSessionsMap(includedData);
  }

  for (const event of eventData) {
    if (event.relationships && event.relationships.sessions) {
      for (const sessionRef of event.relationships.sessions.data) {
        const session = sessionsMap.get(sessionRef.id);
        if (session) {
          const timeArray = session.attributes.estimated_started_at;
          const timestamp = dayjsWithTz()
            .year(timeArray[0])
            .month(timeArray[1] - 1)
            .date(timeArray[2])
            .hour(timeArray[3])
            .minute(timeArray[4])
            .second(0)
            .unix();

          events.push({
            time: timestamp,
            title: event.attributes.title,
            description: event.attributes.description,
            registration_link: event.attributes.registration_link,
            duration: event.attributes.estimated_duration,
            language: event.attributes.language,
          });
        }
      }
    }
  }
  findClosestEvent(events);
  intervalId = setInterval(() => {
    currentTimeInSeconds.value = dayjsWithTz().unix();
    findClosestEvent(events);
  }, 1000);
}

onMounted(async () => {
  await loadAndProcessEvents();
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>
